/**
 * Loosely-typed accessors over the shared `openapi-fetch` Platform API client.
 *
 * The generated ingestion responses are typed as opaque JSON objects
 * (`{ [key: string]: unknown }`), so the typed client adds no response safety
 * for this domain. These thin wrappers accept the path as a string and the
 * standard `openapi-fetch` options bag, letting each service build paths from a
 * shared constant and cast the response to the concrete shape it expects.
 *
 * Runtime behaviour is identical to the typed client — `openapi-fetch` still
 * performs `{path_param}` substitution from `params.path` and serialises
 * `params.query`, auth + `X-Request-ID` are still attached by the middleware in
 * `@/lib/platform-api`.
 */

import { platformApi } from '@/lib/platform-api';

export interface PlatformRequestOptions {
  params?: { path?: Record<string, string>; query?: Record<string, unknown> };
  body?: unknown;
  headers?: Record<string, string>;
  /** Forwarded to fetch by openapi-fetch. Lets a caller bound a request it cannot abandon. */
  signal?: AbortSignal;
}

export interface PlatformResponse {
  data?: unknown;
  error?: unknown;
  response?: Response;
}

type PlatformCall = (path: string, options?: PlatformRequestOptions) => Promise<PlatformResponse>;

export const platformGet = platformApi.GET as unknown as PlatformCall;
export const platformPost = platformApi.POST as unknown as PlatformCall;
export const platformPatch = platformApi.PATCH as unknown as PlatformCall;
export const platformPut = platformApi.PUT as unknown as PlatformCall;
export const platformDelete = platformApi.DELETE as unknown as PlatformCall;

/** Standard Idempotency-Key header for mutations (Platform API requires it). */
export function idempotencyHeaders(): Record<string, string> {
  return { 'Idempotency-Key': crypto.randomUUID() };
}

interface PydanticValidationError {
  loc: (string | number)[];
  msg: string;
}

function isPydanticValidationErrors(detail: unknown): detail is PydanticValidationError[] {
  return Array.isArray(detail) && detail.every((entry) => entry && typeof entry === 'object' && 'loc' in entry && 'msg' in entry);
}

/** Extract a human-readable message from a Platform API error envelope. */
export function platformErrorMessage(error: unknown, fallback: string): string {
  const err = error as { detail?: unknown; message?: unknown; error?: { message?: unknown } } | undefined;
  const { detail } = err ?? {};
  if (isPydanticValidationErrors(detail)) {
    return detail.map((e) => `${e.loc.map(String).join('.')}: ${e.msg}`).join('; ');
  }
  return String(detail ?? err?.error?.message ?? err?.message ?? fallback);
}

/** Platform API failure that carries the HTTP status, so callers can tell 403 from 503. */
export class PlatformRequestError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly body?: unknown
  ) {
    super(message);
    this.name = 'PlatformRequestError';
  }
}
